import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

const badgeVariants = cva(
  "inline-flex items-center rounded-sm border px-2 py-0.5 font-medium text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-accent text-accent-foreground hover:bg-accent-hover",
        secondary:
          "border-transparent bg-cream-deep text-ink-2 hover:bg-cream-deep/80",
        outline: "border-hairline text-ink-1",
        ghost: "border-transparent text-ink-2",
        // ``text-warning-foreground`` is the DARK accent (hsl(22 85% 32%)) — it
        // clears WCAG AA (~5.9:1) on the soft-orange tint where the bright
        // ``text-accent`` (~2.2:1) failed. Icons inside stay legible at 3:1.
        accent: "border-transparent bg-ando-orange-soft text-warning-foreground",
        success: "border-transparent bg-success/15 text-success-foreground",
        warning: "border-transparent bg-warning/15 text-warning-foreground",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
);
Badge.displayName = "Badge";

export { Badge, badgeVariants };
